import React, { useContext, useState } from 'react';
import { motion, AnimatePresence } from "framer-motion";

import useGateway from '../model/Gateway'

import Devices from "./main-pages/devices/Devices";
import Settings from "./main-pages/settings/Settings";
import Scenarios from "./main-pages/scenarios/Scenarios";
import Reports from "./main-pages/reports/Reports";
import InsightsAndCharts from './main-pages/reports/InsightsAndCharts';
import HomeTab from "./home-components/home-tab/HomeTab";
import Binds from "./main-pages/binds/Binds";
import ProfileHeader from "./home-components/profile-header/ProfileHeader";
import ConnectionStatus from "./home-components/connection-status/ConnectionStatus"
import OverlayPages, { overlayPageAnimation } from './overlay-pages/OverlayPages';
import { LocalizationContext } from 'app/localization/Context';
import { HubUpdatePopup, AvailabilityErrorPopup, ClientUpdatePopup, AskPushPopup } from './popups/Popups';
import { HistoryStackContext } from './general-components/HistoryStackContext';

import "./Home.scss";
import "./Background.scss"

const animationPageVariants = {
  initial: {
    opacity: 0,
    x: '10px'
  },
  animate: {
    opacity: 1,
    x: 0
  },
  exit: {
    opacity: 0,
    x: '-10px'
  }
};

const animationTransition = {
  duration: 0.25
}

function AnimationContainer(props) {
  return (
    <motion.div
      initial="initial"
      animate="animate"
      exit="exit"
      variants={animationPageVariants}
      transition={animationTransition}

      className="home-page-content"
    >
      {props.children}
    </motion.div>
  );
}

function Home({ homesManager, profile, swController, windowWidth, windowHeight }) {
  const inWideView = windowWidth >= 800;

  const gateway = useGateway(homesManager, profile);


  return (
    <GatewayDataProvider gateway={gateway}>
      <FirstContainer windowWidth={windowWidth} windowHeight={windowHeight}>
        <ConnectionStatus connectionStatus={gateway.connectionStatus} />

        <div className="second-container">
          <div className="bg-primary-color" />
          <HomeContent homesManager={homesManager} gateway={gateway} inWideView={inWideView} windowWidth={windowWidth} />
          <OverlayPages homesManager={homesManager} gateway={gateway} profile={profile} swController={swController} inWideView={inWideView} />
          <AvailabilityErrorPopup
            homeAvailabilityCriticalError={gateway.homeAvailabilityCriticalError}
            logoutFromSolo={homesManager.logoutFromSolo} />
          <HubUpdatePopup homesManager={homesManager} gateway={gateway} />
          <ClientUpdatePopup swController={swController} />
          <AskPushPopup swController={swController} />
        </div>
      </FirstContainer>
    </GatewayDataProvider>
  );
}

export function FirstContainer({ children, windowWidth, windowHeight }) {
  const l = useContext(LocalizationContext);

  return (
    <div className="first-container"
      dir={l.isRTL ? "rtl" : "ltr"}
      lang={l.language}
      style={{ "--window-width": String(windowWidth) + "px", "--window-height": String(windowHeight) + "px" }}>
      <div className="bg-blank" />
      {children}
    </div>
  )
}

export const GatewayDataContext = React.createContext(undefined);
function GatewayDataProvider({ gateway, children }) {
  return (
    <GatewayDataContext.Provider value={{ gateway }}>
      {children}
    </GatewayDataContext.Provider>
  )
}

function HomeContent({ homesManager, gateway, inWideView, windowWidth }) {
  const hs = useContext(HistoryStackContext);
  const activateHorizontalAnimation = (!inWideView) && (overlayPageAnimation[hs.bottomPagePath] === "horizontal");
  const [page, setPage] = useState("devices");

  return (
    <div className={
      activateHorizontalAnimation ?
        "home-content when-horizontal-overlay-page-is-active"
        : "home-content"}>
      <ProfileHeader
        homesManager={homesManager}
        devices={gateway.devices}
        negotiatedProtocol={gateway.negotiatedProtocol}
        aiChatStartListening={gateway.aiChat.startListening} />

      <HomePages gateway={gateway} page={page} windowWidth={windowWidth} />
      <HomeTab gateway={gateway} page={page} setPage={setPage} />
    </div>
  );
}

function HomePages({ gateway, page, windowWidth }) {
  return (
    <AnimatePresence mode='wait'>
      {(page === "devices") ? (
        <AnimationContainer key="page_devices">
          <Devices
            roomsData={gateway.roomsData} roomsController={gateway.roomsController}
            dataFetched={gateway.dataFetched}
            devices={gateway.devices} currentUser={gateway.currentUser}
            negotiatedProtocol={gateway.negotiatedProtocol}
            showDeleteAllDevicesButton={gateway.showDeleteAllDevicesButton} />
        </AnimationContainer>
      ) : null}

      {(page === "scenarios") ? (
        <AnimationContainer key="page_scenarios">
          <Scenarios
            dataFetched={gateway.dataFetched}
            scenarios={gateway.scenarios}
            currentUser={gateway.currentUser}
            scenarioReports={gateway.scenarioReports} />
        </AnimationContainer>
      ) : null}

      {(page === "binds") ? (
        <AnimationContainer key="page_binds">
          <Binds
            dataFetched={gateway.dataFetched}
            binds={gateway.binds}
            devices={gateway.devices}
            scenarios={gateway.scenarios}
            currentUser={gateway.currentUser}
            negotiatedProtocol={gateway.negotiatedProtocol} />
        </AnimationContainer>
      ) : null}

      {(page === "reports") ? (
        <AnimationContainer key="page_reports">
          {gateway.negotiatedProtocol.supportInsightsAndCharts ?
            <InsightsAndCharts
              devices={gateway.devices}
              allUsers={gateway.allUsers}
              insights={gateway.insights}
              allLampsChart={gateway.allLampsChart}
              stateChartPerMac={gateway.stateChartPerMac}
              windowWidth={windowWidth} />
            : <Reports gateway={gateway} />
          }

        </AnimationContainer>
      ) : null}

      {(page === "settings") ? (
        <AnimationContainer key="page_settings">
          <Settings
            devices={gateway.devices}
            allUsers={gateway.allUsers}
            gwUpdateInfo={gateway.gwUpdateInfo}
            negotiatedProtocol={gateway.negotiatedProtocol} />
        </AnimationContainer>
      ) : null}
    </AnimatePresence>
  )
}

export default Home;
